import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, ExternalLink, Handshake, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { formatDate, formatRelative } from "@/lib/utils";
import type { PortalDealRow } from "@colab/shared";
import { useDealsList } from "./use-deals-list";
import { DealStatusBadge } from "./deal-status-badge";
import { AttributionDirectionBadge } from "./attribution-direction-badge";
import { InboundVsOutboundSummary } from "./inbound-vs-outbound-summary";
import { formatCurrency } from "./format";

// Phase 7 sub-bucket 5 — partner-portal deals list. Rich rebuild beyond the
// legacy `/app/portal/deals/page.tsx` per user decision: tab strip (Open /
// Closed / All) replaces the legacy `?showClosed=1` toggle; aggregate cards
// split inbound vs outbound (NEVER blended — bucket signature constraint);
// cards drill into a detail page rather than linking out to HubSpot
// directly (attribution rows still link to HubSpot via a tertiary action).

type Segment = "open" | "closed" | "all";

const SEGMENTS: ReadonlyArray<{
  id: Segment;
  label: string;
  match: (row: PortalDealRow) => boolean;
  emptyTitle: string;
  emptyBody: string;
}> = [
  {
    id: "open",
    label: "Open",
    match: (r) => !r.is_closed,
    emptyTitle: "No open deals",
    emptyBody:
      "Submit a lead or wait for Bookboost to attribute a HubSpot deal to your team.",
  },
  {
    id: "closed",
    label: "Closed",
    match: (r) => r.is_closed,
    emptyTitle: "No closed deals",
    emptyBody: "Won, lost, and withdrawn deals land here for reference.",
  },
  {
    id: "all",
    label: "All",
    match: () => true,
    emptyTitle: "No deals yet",
    emptyBody:
      "Submit a lead or wait for Bookboost to attribute a HubSpot deal to your team.",
  },
];

export function DealsListPage() {
  const list = useDealsList();
  const allRows = list.data ?? [];

  const counts = useMemo(() => {
    return {
      open: allRows.filter((r) => !r.is_closed).length,
      closed: allRows.filter((r) => r.is_closed).length,
      all: allRows.length,
    } as Record<Segment, number>;
  }, [allRows]);

  const defaultSegment: Segment = counts.open > 0 ? "open" : "all";
  const [segment, setSegment] = useState<Segment>(defaultSegment);

  return (
    <div>
      <header className="mb-7 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Partnership
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            My deals
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Leads you&rsquo;ve submitted and HubSpot deals Bookboost has
            attributed to your team.
          </p>
        </div>
        <Button asChild>
          <Link to="/portal/deals/new" className="inline-flex items-center gap-1.5">
            <Plus className="h-4 w-4" />
            Submit a lead
          </Link>
        </Button>
      </header>

      <div className="mb-8">
        <InboundVsOutboundSummary rows={allRows} />
      </div>

      <Tabs
        value={segment}
        onValueChange={(v) => setSegment(v as Segment)}
        className="w-full"
      >
        <TabsList>
          {SEGMENTS.map((s) => (
            <TabsTrigger key={s.id} value={s.id}>
              {s.label}
              <span className="ml-1.5 text-[11px] text-muted-foreground">
                {counts[s.id]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {SEGMENTS.map((s) => {
          const rows = allRows.filter(s.match);
          return (
            <TabsContent key={s.id} value={s.id} className="mt-6">
              {list.isLoading ? (
                <SkeletonList />
              ) : list.isError ? (
                <ErrorPanel />
              ) : rows.length === 0 ? (
                <EmptyPanel title={s.emptyTitle} body={s.emptyBody} />
              ) : (
                <ul className="space-y-3">
                  {rows.map((row) => (
                    <li key={`${row.source}-${row.id}`}>
                      <DealCard row={row} />
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

function DealCard({ row }: { row: PortalDealRow }) {
  return (
    <div className="group relative rounded-xl border bg-card p-4 transition hover:border-accent">
      {/* Full-card click target → in-Colab detail. Sits behind the content;
          interactive children (the HubSpot link) opt back in with
          pointer-events-auto so only the ↗ icon escapes to HubSpot. */}
      <Link
        to={`/portal/deals/${row.id}`}
        className="absolute inset-0 z-0 rounded-xl"
        aria-label={`View ${row.customer_name}`}
      />
      <div className="pointer-events-none relative z-10 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-foreground transition group-hover:text-accent">
              {row.customer_name}
            </span>
            <DealStatusBadge row={row} />
            <AttributionDirectionBadge row={row} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {row.source === "deal"
              ? "Lead you submitted"
              : "Attributed in HubSpot"}
            {row.updated_at ? (
              <> · Updated {formatRelative(row.updated_at)}</>
            ) : null}
            {row.close_date ? (
              <> · Close {formatDate(row.close_date)}</>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="font-display text-base font-semibold tabular-nums">
            {formatCurrency(row.amount, row.currency_code)}
          </span>
          {row.hubspot_url ? (
            <a
              href={row.hubspot_url}
              target="_blank"
              rel="noreferrer noopener"
              onClick={(e) => e.stopPropagation()}
              className="pointer-events-auto text-muted-foreground transition hover:text-accent"
              title="Open in HubSpot"
              aria-label="Open in HubSpot"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : null}
          <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-accent" />
        </div>
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-20 rounded-xl" />
        </li>
      ))}
    </ul>
  );
}

function EmptyPanel({ title, body }: { title: string; body: string }) {
  return (
    <section className="rounded-xl border bg-card p-10 text-center">
      <Handshake className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
        {title}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      <Link
        to="/portal/deals/new"
        className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
      >
        Submit a lead
        <ArrowRight className="h-3 w-3" />
      </Link>
    </section>
  );
}

function ErrorPanel() {
  return (
    <section className="rounded-xl border bg-card p-10 text-center">
      <Loader2 className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
        Couldn&rsquo;t load deals
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Please refresh the page. If the problem persists, contact support.
      </p>
    </section>
  );
}
